import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "../components/ui/dropdown-menu";
import { 
  Search, FileText, Calendar, Euro, Building2, Filter, 
  ChevronLeft, ChevronRight, Eye, Link2, Download, RefreshCw, Upload,
  Plus, MoreVertical, Pencil, Trash2, Paperclip, ArrowUpAZ, ArrowDownAZ, ArrowUpDown,
  ChevronDown, FileSpreadsheet, X, CheckSquare, GripVertical
} from "lucide-react";
import * as XLSX from 'xlsx';
import ImportContractsModal from "../components/ImportContractsModal";
import ContractFormModal from "../components/ContractFormModal";
import PDFViewerModal from "../components/PDFViewerModal";
import ColumnConfigPopover, { loadColumnsConfig, saveColumnsConfig, reorderColumns, DEFAULT_COLUMNS } from "../components/ColumnConfigPopover";
import ContractViewsSelector, { applyViewToColumns, loadActiveViewId, saveActiveViewId } from "../components/ContractViewsSelector";
import { MultiSelectFilter } from "../components/ui/multi-select-filter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  if (!value && value !== 0) return '-';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatDateFR = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' });
};

const getStatusBadge = (status, statut_resiliation, reconduction_state, tacite_reconduction) => {
  // Récupère le badge de base (résiliation ou statut normal)
  let baseBadge = null;
  if (statut_resiliation === 'resiliation_en_cours') {
    baseBadge = <Badge className="bg-orange-500 text-white border-orange-600" data-testid="badge-resiliation-en-cours">Résiliation en cours</Badge>;
  } else if (statut_resiliation === 'resilier' || statut_resiliation === 'resilie') {
    baseBadge = <Badge className="bg-gray-500 text-white border-gray-600" data-testid="badge-resilie">Résilié</Badge>;
  } else {
    switch (status) {
      case 'active':
        baseBadge = <Badge className="bg-green-100 text-green-700 border-green-200">Actif</Badge>;
        break;
      case 'expiring_soon':
        baseBadge = <Badge className="bg-orange-100 text-orange-700 border-orange-200">Expire bientôt</Badge>;
        break;
      case 'expired':
        baseBadge = <Badge className="bg-red-100 text-red-700 border-red-200">Expiré</Badge>;
        break;
      default:
        baseBadge = <Badge variant="outline">Inconnu</Badge>;
    }
  }

  // Badge additionnel pour l'état de reconduction (cumulatif avec le baseBadge)
  let reconductionBadge = null;
  if (reconduction_state === "en_reconduction") {
    reconductionBadge = <Badge className="bg-purple-100 text-purple-800 border-purple-300" data-testid="badge-en-reconduction" title="Ce contrat a été reconduit tacitement">🔁 Reconduit tacitement</Badge>;
  } else if (reconduction_state === "reconduction_a_confirmer") {
    reconductionBadge = <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid="badge-a-confirmer" title="Reconduction potentielle détectée à confirmer">⚠️ Reconduction à confirmer</Badge>;
  } else if (tacite_reconduction) {
    reconductionBadge = <Badge className="bg-purple-50 text-purple-700 border-purple-200" title="Contrat à tacite reconduction — surveillé">🔁 Tacite</Badge>;
  }

  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      {baseBadge}
      {reconductionBadge}
    </span>
  );
};

const _unusedOldGetStatusBadge = (status, statut_resiliation) => {
  // Kept for reference — not used
  return null;
};

const getTypeBadge = (type) => {
  const colors = {
    'SAUVEGARDE': 'bg-green-100 text-green-700 border-green-200',
    'CYBERSECURITE': 'bg-cyan-100 text-cyan-700 border-cyan-200',
    'CYBERSÉCURITÉ': 'bg-cyan-100 text-cyan-700 border-cyan-200',
  };
  return <Badge className={colors[type] || 'bg-gray-100 text-gray-700 border-gray-200'}>{type}</Badge>;
};

// État indicator component - Simple colored dot with file count
// Green: files in DOCUMENTS CONTRACTUELS
// Orange: files in ÉCHÉANCIER and/or ANCIENS CONTRATS (but not in DOCUMENTS CONTRACTUELS)
// Red: no files in any folder
const EtatIndicator = ({ etat }) => {
  if (!etat) return <span className="text-gray-400">-</span>;
  
  const activeColor = etat.color;
  const count = etat.count || 0;
  
  // Determine dot color and style
  const getDotStyle = () => {
    switch (activeColor) {
      case 'green':
        return 'bg-emerald-500 shadow-[0_0_8px_2px_rgba(16,185,129,0.5)]';
      case 'orange':
        return 'bg-amber-500 shadow-[0_0_8px_2px_rgba(245,158,11,0.5)]';
      case 'red':
        return 'bg-rose-500 shadow-[0_0_8px_2px_rgba(244,63,94,0.5)]';
      default:
        return 'bg-slate-400';
    }
  };
  
  const getCountStyle = () => {
    switch (activeColor) {
      case 'green':
        return 'bg-emerald-100 text-emerald-700';
      case 'orange':
        return 'bg-amber-100 text-amber-700';
      case 'red':
        return 'bg-rose-100 text-rose-700';
      default:
        return 'bg-slate-100 text-slate-500';
    }
  };
  
  return (
    <div className="inline-flex items-center gap-2">
      {/* Colored dot */}
      <div className={`w-3.5 h-3.5 rounded-full transition-all ${getDotStyle()}`} />
      {/* Count badge - only show if count > 0 */}
      {count > 0 && (
        <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${getCountStyle()}`}>
          {count}
        </span>
      )}
    </div>
  );
};

// État filter options
const ETAT_OPTIONS = [
  { value: 'green', label: 'Complet', color: 'bg-emerald-500' },
  { value: 'orange', label: 'En cours', color: 'bg-amber-500' },
  { value: 'red', label: 'Vide', color: 'bg-rose-500' }
];

// Résiliation filter options (sent to backend as statut_resiliation param)
const RESILIATION_OPTIONS = [
  { value: 'resiliation_en_cours', label: 'Résiliation en cours', color: 'bg-orange-500' },
  { value: 'resilier', label: 'Résilié', color: 'bg-gray-500' }
];

export default function ContratsAnnuaire() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalGestionInterne, setTotalGestionInterne] = useState(null);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(50);
  
  // Filter states - now arrays for multi-select
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState([]);
  const [etatFilter, setEtatFilter] = useState([]);
  const [resiliationFilter, setResiliationFilter] = useState([]);
  const [clientFilter, setClientFilter] = useState([]);
  const [produitFilter, setProduitFilter] = useState([]);
  const [vendeurFilter, setVendeurFilter] = useState([]);
  const [revendeurFilter, setRevendeurFilter] = useState([]);
  const [leaserFilter, setLeaserFilter] = useState([]);
  const [yearFilter, setYearFilter] = useState("all");
  const [gestionInterneFilter, setGestionInterneFilter] = useState("all");
  
  // Detail modal
  const [selectedContract, setSelectedContract] = useState(null);
  const [clientLink, setClientLink] = useState(null);
  const [loadingLink, setLoadingLink] = useState(false);
  
  // Invoice detail/PDF modal
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  
  // Import modal
  const [importModalOpen, setImportModalOpen] = useState(false);
  
  // Create/Edit modal
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Selection for export
  const [selectedContracts, setSelectedContracts] = useState(new Set());
  
  // Sorting
  const [sortOrder, setSortOrder] = useState(null); // null, 'asc', 'desc'
  
  // Column configuration
  const [columns, setColumns] = useState(() => loadColumnsConfig());
  const [activeViewId, setActiveViewId] = useState(() => loadActiveViewId());
  const [draggedColId, setDraggedColId] = useState(null);
  const [dragOverColId, setDragOverColId] = useState(null);

  // Sélectionne une vue → applique ses colonnes et sauvegarde
  const handleSelectView = (view) => {
    const newColumns = applyViewToColumns(DEFAULT_COLUMNS, view);
    setColumns(newColumns);
    saveColumnsConfig(newColumns);
    setActiveViewId(view.id);
    saveActiveViewId(view.id);
  };

  // Drag & drop directement sur les en-têtes du tableau (comme Excel)
  const handleHeaderDragStart = (e, colId) => {
    setDraggedColId(colId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', colId);
  };
  const handleHeaderDragOver = (e, colId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (colId !== draggedColId) setDragOverColId(colId);
  };
  const handleHeaderDragLeave = () => setDragOverColId(null);
  const handleHeaderDrop = (e, dropColId) => {
    e.preventDefault();
    if (!draggedColId || draggedColId === dropColId) {
      setDraggedColId(null);
      setDragOverColId(null);
      return;
    }
    const dragIndex = columns.findIndex(c => c.id === draggedColId);
    const dropIndex = columns.findIndex(c => c.id === dropColId);
    const newColumns = reorderColumns(columns, dragIndex, dropIndex);
    setColumns(newColumns);
    saveColumnsConfig(newColumns);
    setDraggedColId(null);
    setDragOverColId(null);
  };
  const handleHeaderDragEnd = () => {
    setDraggedColId(null);
    setDragOverColId(null);
  };

  const fetchFilters = useCallback(async () => {
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/contracts/filters`, { headers });
      setFilters(res.data);
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  }, [token]);

  const fetchContracts = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams();
      
      if (search) params.append('search', search);
      // Multi-select filters - join values with comma
      if (typeFilter.length > 0) params.append('type', typeFilter.join(','));
      if (etatFilter.length > 0) params.append('etat', etatFilter.join(','));
      if (resiliationFilter.length > 0) params.append('statut_resiliation', resiliationFilter.join(','));
      if (clientFilter.length > 0) params.append('client', clientFilter.join(','));
      if (produitFilter.length > 0) params.append('produit', produitFilter.join(','));
      if (vendeurFilter.length > 0) params.append('vendeur', vendeurFilter.join(','));
      if (revendeurFilter.length > 0) params.append('revendeur', revendeurFilter.join(','));
      if (leaserFilter.length > 0) params.append('leaser', leaserFilter.join(','));
      if (yearFilter && yearFilter !== 'all') params.append('year', yearFilter);
      if (gestionInterneFilter && gestionInterneFilter !== 'all') params.append('gestion_interne', gestionInterneFilter);
      params.append('limit', perPage);
      params.append('skip', page * perPage);
      
      const res = await axios.get(`${API}/contracts?${params}`, { headers });
      setContracts(res.data.contracts);
      setTotal(res.data.total);
      setTotalGestionInterne(res.data.total_gestion_interne ?? null);
    } catch (error) {
      console.error("Error fetching contracts:", error);
    } finally {
      setLoading(false);
    }
  }, [token, search, typeFilter, etatFilter, resiliationFilter, clientFilter, produitFilter, vendeurFilter, revendeurFilter, leaserFilter, yearFilter, gestionInterneFilter, page, perPage]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchContracts();
  };

  const handleViewLink = async (clientName) => {
    setLoadingLink(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/contracts/link/${encodeURIComponent(clientName)}`, { headers });
      setClientLink(res.data);
    } catch (error) {
      console.error("Error fetching client link:", error);
    } finally {
      setLoadingLink(false);
    }
  };

  const resetFilters = () => {
    setSearch("");
    setTypeFilter([]);
    setEtatFilter([]);
    setResiliationFilter([]);
    setClientFilter([]);
    setProduitFilter([]);
    setVendeurFilter([]);
    setRevendeurFilter([]);
    setLeaserFilter([]);
    setYearFilter("all");
    setGestionInterneFilter("all");
    setPage(0);
    setSortOrder(null);
  };

  // Get sorted contracts
  const sortedContracts = sortOrder 
    ? [...contracts].sort((a, b) => {
        const nameA = (a.societe || '').toLowerCase();
        const nameB = (b.societe || '').toLowerCase();
        if (sortOrder === 'asc') {
          return nameA.localeCompare(nameB, 'fr');
        } else {
          return nameB.localeCompare(nameA, 'fr');
        }
      })
    : contracts;

  const handleCreateContract = () => {
    setEditingContract(null);
    setFormModalOpen(true);
  };

  const handleEditContract = (contract) => {
    setEditingContract(contract);
    setFormModalOpen(true);
  };

  const handleDeleteContract = async (contract) => {
    if (!window.confirm(`Supprimer le contrat de "${contract.societe}" ?\n\nCette action supprimera également tous les documents associés.`)) {
      return;
    }

    setDeleting(contract.id);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/contracts/${contract.id}`, { headers });
      fetchContracts();
    } catch (error) {
      console.error("Error deleting contract:", error);
      alert("Erreur lors de la suppression");
    } finally {
      setDeleting(null);
    }
  };

  const handleFormSuccess = () => {
    fetchContracts();
    fetchFilters();
  };

  // Get unique clients for filter
  const uniqueClients = [...new Set(contracts.map(c => c.societe))].sort();
  
  // Get years from date_fin
  const getYears = () => {
    const years = new Set();
    contracts.forEach(c => {
      if (c.date_fin) {
        const year = new Date(c.date_fin).getFullYear();
        if (year >= 2020 && year <= 2035) years.add(year);
      }
    });
    return Array.from(years).sort((a, b) => a - b);
  };

  const totalPages = Math.ceil(total / perPage);

  const handleImportSuccess = () => {
    fetchContracts();
    fetchFilters();
  };

  // Selection handlers
  const toggleSelectContract = (contractId) => {
    setSelectedContracts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(contractId)) {
        newSet.delete(contractId);
      } else {
        newSet.add(contractId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedContracts.size === contracts.length) {
      // Deselect all
      setSelectedContracts(new Set());
    } else {
      // Select all visible contracts
      setSelectedContracts(new Set(contracts.map(c => c.id)));
    }
  };

  const clearSelection = () => {
    setSelectedContracts(new Set());
  };

  // Export contracts to Excel
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      let contractsToExport = [];
      
      // If contracts are selected, export only those
      if (selectedContracts.size > 0) {
        contractsToExport = contracts.filter(c => selectedContracts.has(c.id));
      } else {
        // Otherwise, fetch all contracts with current filters (no pagination)
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        if (typeFilter.length > 0) params.append('type', typeFilter.join(','));
        if (etatFilter.length > 0) params.append('etat', etatFilter.join(','));
        if (resiliationFilter.length > 0) params.append('statut_resiliation', resiliationFilter.join(','));
        if (clientFilter.length > 0) params.append('client', clientFilter.join(','));
        if (produitFilter.length > 0) params.append('produit', produitFilter.join(','));
        if (vendeurFilter.length > 0) params.append('vendeur', vendeurFilter.join(','));
        if (revendeurFilter.length > 0) params.append('revendeur', revendeurFilter.join(','));
        if (leaserFilter.length > 0) params.append('leaser', leaserFilter.join(','));
        if (yearFilter && yearFilter !== 'all') params.append('year', yearFilter);
        if (gestionInterneFilter && gestionInterneFilter !== 'all') params.append('gestion_interne', gestionInterneFilter);
        params.append('skip', '0');
        params.append('limit', '10000'); // Get all
        
        const res = await axios.get(`${API}/contracts?${params}`, { headers });
        contractsToExport = res.data.contracts || [];
      }
      
      if (contractsToExport.length === 0) {
        alert('Aucun contrat à exporter');
        return;
      }
      
      // Prepare data for Excel
      const excelData = contractsToExport.map(c => ({
        'Société / Client': c.societe || '',
        'Type': c.type || '',
        'État': c.etat?.label || '',
        'Produit': c.produit || '',
        'Vendeur': c.vendeur || '',
        'Revendeur': c.revendeur || '',
        'Leaser': c.leaser || '',
        'Date Signature': c.date_signature ? new Date(c.date_signature).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' }) : '',
        'Date Installation': c.date_installation ? new Date(c.date_installation).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' }) : '',
        'Date Fin': c.date_fin ? new Date(c.date_fin).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' }) : '',
        'Durée (mois)': c.duree || '',
        'MRR (€)': c.mrr || 0,
        'CA Leaser (€)': c.ca_leaser || 0,
        'Abonnement (€)': c.abonnement_mensuel || 0,
        'Gestion Interne (€)': c.gestion_interne || 0,
        'Finance Leasing (€)': c.finance_leasing || 0,
        'N° Financement': c.numero_financement || '',
        'Statut': c.status === 'active' ? 'Actif' : c.status === 'expired' ? 'Expiré' : c.status === 'expiring_soon' ? 'Expire bientôt' : c.status || ''
      }));
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // Set column widths
      const colWidths = [
        { wch: 30 }, // Société
        { wch: 15 }, // Type
        { wch: 12 }, // État
        { wch: 20 }, // Produit
        { wch: 15 }, // Vendeur
        { wch: 15 }, // Revendeur
        { wch: 15 }, // Leaser
        { wch: 12 }, // Date Signature
        { wch: 12 }, // Date Installation
        { wch: 12 }, // Date Fin
        { wch: 10 }, // Durée
        { wch: 12 }, // MRR
        { wch: 12 }, // CA Leaser
        { wch: 12 }, // Abonnement
        { wch: 12 }, // Gestion Interne
        { wch: 12 }, // Finance Leasing
        { wch: 15 }, // N° Financement
        { wch: 12 }  // Statut
      ];
      ws['!cols'] = colWidths;
      
      XLSX.utils.book_append_sheet(wb, ws, 'Contrats');
      
      // Generate filename with date
      const date = new Date().toISOString().split('T')[0];
      const filename = `contrats_export_${date}.xlsx`;
      
      // Download
      XLSX.writeFile(wb, filename);
      
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      alert("Erreur lors de l'export Excel");
    } finally {
      setExporting(false);
    }
  };

  // Get visible columns
  const visibleColumns = columns.filter(col => col.visible);
  const hiddenColumns = columns.filter(col => !col.visible);

  // Toggle column visibility inline (X icon to hide, + menu to add)
  const setColumnVisible = (colId, visible) => {
    const newColumns = columns.map(c => c.id === colId ? { ...c, visible } : c);
    setColumns(newColumns);
    saveColumnsConfig(newColumns);
  };

  // Render cell content based on column id
  const renderCell = (contract, columnId) => {
    switch (columnId) {
      case 'societe':
        return (
          <span 
            className="font-medium text-foreground max-w-[200px] truncate cursor-pointer hover:text-blue-600 hover:underline block"
            onClick={() => navigate(`/contrats/${contract.id}`)}
            title="Voir les détails"
          >
            {contract.societe}
          </span>
        );
      case 'type':
        return getTypeBadge(contract.type);
      case 'etat':
        return <EtatIndicator etat={contract.etat} />;
      case 'produit':
        return <span className="text-muted-foreground text-sm">{contract.produit || '-'}</span>;
      case 'vendeur':
        return <span className="text-muted-foreground text-sm">{contract.vendeur || '-'}</span>;
      case 'revendeur':
        return <span className="text-muted-foreground text-sm">{contract.revendeur || '-'}</span>;
      case 'leaser':
        return contract.leaser && contract.leaser !== 'NON' ? (
          <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
            {contract.leaser}
          </Badge>
        ) : (
          <span className="text-muted-foreground text-sm">-</span>
        );
      case 'date_fin':
        return <span className="text-sm whitespace-nowrap">{formatDateFR(contract.date_fin)}</span>;
      case 'mrr':
        return <span className="font-mono text-sm">{formatCurrency(contract.montant_abonnement)}</span>;
      case 'ca_leaser':
        return <span className="font-mono text-sm">{formatCurrency(contract.ca_leaser)}</span>;
      case 'status':
        return getStatusBadge(contract.status, contract.statut_resiliation, contract.reconduction_state, contract.tacite_reconduction);
      case 'date_installation':
        return <span className="text-sm whitespace-nowrap">{formatDateFR(contract.date_installation)}</span>;
      case 'duree_mois':
        return <span className="text-sm">{contract.duree_mois ? `${contract.duree_mois} mois` : '-'}</span>;
      case 'commission':
        return <span className="font-mono text-sm">{formatCurrency(contract.commission)}</span>;
      case 'abonnement_gi':
        return <span className="font-mono text-sm">{formatCurrency(contract.abonnement_gestion_interne)}</span>;
      case 'abonnement_fin':
        return <span className="font-mono text-sm">{formatCurrency(contract.abonnement_finance)}</span>;
      case 'date_demande_resiliation':
        return contract.date_demande_resiliation ? (
          <span className="text-sm whitespace-nowrap">{formatDateFR(contract.date_demande_resiliation)}</span>
        ) : (
          <span className="text-muted-foreground text-sm">-</span>
        );
      case 'date_effet_resiliation':
        return contract.date_effet_resiliation ? (
          <span className={`text-sm whitespace-nowrap font-medium ${contract.statut_resiliation === 'resiliation_en_cours' ? 'text-orange-600' : ''}`}>
            {formatDateFR(contract.date_effet_resiliation)}
          </span>
        ) : (
          <span className="text-muted-foreground text-sm">-</span>
        );
      default:
        return '-';
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="contrats-annuaire">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Contrats</h1>
          <p className="text-muted-foreground text-sm">{total} contrat{total > 1 ? 's' : ''} trouvé{total > 1 ? 's' : ''}</p>
          {gestionInterneFilter !== 'all' && totalGestionInterne !== null && (
            <Badge data-testid="total-gestion-interne-badge" className="mt-1 bg-blue-100 text-blue-800 hover:bg-blue-100 font-mono">
              Total Gestion Interne : {formatCurrency(totalGestionInterne)} /mois
            </Badge>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button 
            onClick={handleCreateContract}
            data-testid="new-contract-btn"
            className="flex-1 sm:flex-none"
          >
            <Plus className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Nouveau contrat</span>
            <span className="sm:hidden">Nouveau</span>
          </Button>
          <Button 
            onClick={() => setImportModalOpen(true)}
            variant="outline"
            data-testid="import-contracts-btn"
            className="flex-1 sm:flex-none"
          >
            <Upload className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Importer Excel</span>
            <span className="sm:hidden">Importer</span>
          </Button>
          <Button 
            onClick={handleExportExcel}
            variant="outline"
            disabled={exporting || contracts.length === 0}
            data-testid="export-contracts-btn"
            className="flex-1 sm:flex-none"
          >
            {exporting ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4 mr-2" />
            )}
            <span className="hidden sm:inline">
              {selectedContracts.size > 0 
                ? `Exporter (${selectedContracts.size})` 
                : 'Exporter Excel'}
            </span>
            <span className="sm:hidden">
              {selectedContracts.size > 0 ? `(${selectedContracts.size})` : 'Exporter'}
            </span>
          </Button>
          <ContractViewsSelector
            activeViewId={activeViewId}
            onSelectView={handleSelectView}
            currentColumns={columns}
          />
          <ColumnConfigPopover columns={columns} setColumns={setColumns} />
        </div>
      </div>

      {/* Selection toolbar */}
      {selectedContracts.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckSquare className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-blue-900">
              {selectedContracts.size} contrat{selectedContracts.size > 1 ? 's' : ''} sélectionné{selectedContracts.size > 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleSelectAll}
              className="text-blue-700 hover:text-blue-900 hover:bg-blue-100"
            >
              {selectedContracts.size === contracts.length ? 'Tout désélectionner' : 'Tout sélectionner'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSelection}
              className="text-blue-700 hover:text-blue-900 hover:bg-blue-100"
            >
              <X className="w-4 h-4 mr-1" />
              Effacer
            </Button>
            <Button
              size="sm"
              onClick={handleExportExcel}
              disabled={exporting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {exporting ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-4 h-4 mr-2" />
              )}
              Exporter la sélection
            </Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-white border">
        <CardContent className="p-3 sm:p-4">
          <form onSubmit={handleSearch} className="space-y-3 sm:space-y-4">
            {/* Row 1: Search + Type + Produit + Client */}
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
              <div className="relative col-span-2 sm:col-span-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              
              <MultiSelectFilter
                values={typeFilter}
                onValuesChange={setTypeFilter}
                options={filters?.types || []}
                allLabel="Tous les types"
                placeholder="Types"
                data-testid="type-filter"
              />
              
              {/* État filter */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className={`w-full justify-between h-9 font-normal ${(etatFilter.length + resiliationFilter.length) > 0 ? 'border-blue-500 bg-blue-50' : 'text-muted-foreground'}`}
                    data-testid="etat-filter"
                  >
                    <span className="truncate">
                      {(etatFilter.length + resiliationFilter.length) === 0 ? 'État' : 
                       (etatFilter.length + resiliationFilter.length) === 1 ? 
                         (etatFilter.length === 1 
                           ? ETAT_OPTIONS.find(o => o.value === etatFilter[0])?.label 
                           : RESILIATION_OPTIONS.find(o => o.value === resiliationFilter[0])?.label) : 
                       `${etatFilter.length + resiliationFilter.length} états`}
                    </span>
                    <ChevronDown className="w-4 h-4 ml-2 opacity-50 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="start">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase px-2 pt-1">Documents</p>
                    {ETAT_OPTIONS.map(option => (
                      <div
                        key={option.value}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-100 ${
                          etatFilter.includes(option.value) ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => {
                          if (etatFilter.includes(option.value)) {
                            setEtatFilter(etatFilter.filter(v => v !== option.value));
                          } else {
                            setEtatFilter([...etatFilter, option.value]);
                          }
                        }}
                      >
                        <Checkbox 
                          checked={etatFilter.includes(option.value)}
                          className="pointer-events-none"
                        />
                        <span className={`w-3 h-3 rounded-full ${option.color}`}></span>
                        <span className="text-sm">{option.label}</span>
                      </div>
                    ))}
                    <p className="text-xs text-muted-foreground uppercase px-2 pt-2 border-t mt-2">Résiliation</p>
                    {RESILIATION_OPTIONS.map(option => (
                      <div
                        key={option.value}
                        data-testid={`resiliation-filter-${option.value}`}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-100 ${
                          resiliationFilter.includes(option.value) ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => {
                          if (resiliationFilter.includes(option.value)) {
                            setResiliationFilter(resiliationFilter.filter(v => v !== option.value));
                          } else {
                            setResiliationFilter([...resiliationFilter, option.value]);
                          }
                        }}
                      >
                        <Checkbox 
                          checked={resiliationFilter.includes(option.value)}
                          className="pointer-events-none"
                        />
                        <span className={`w-3 h-3 rounded-full ${option.color}`}></span>
                        <span className="text-sm">{option.label}</span>
                      </div>
                    ))}
                    {(etatFilter.length > 0 || resiliationFilter.length > 0) && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="w-full mt-2 text-xs"
                        onClick={() => { setEtatFilter([]); setResiliationFilter([]); }}
                      >
                        Effacer
                      </Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              
              <MultiSelectFilter
                values={produitFilter}
                onValuesChange={setProduitFilter}
                options={filters?.produits || []}
                allLabel="Tous les produits"
                placeholder="Produits"
                data-testid="produit-filter"
              />
              
              <MultiSelectFilter
                values={clientFilter}
                onValuesChange={setClientFilter}
                options={filters?.clients || []}
                allLabel="Tous les clients"
                placeholder="Clients"
                data-testid="client-filter"
              />
              
              <Select value={gestionInterneFilter} onValueChange={setGestionInterneFilter}>
                <SelectTrigger 
                  data-testid="gestion-interne-filter"
                  className={`h-9 font-normal ${gestionInterneFilter !== 'all' ? 'border-blue-500 bg-blue-50' : 'text-muted-foreground'}`}
                >
                  <SelectValue placeholder="Gestion Interne" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Gestion Interne : Tous</SelectItem>
                  <SelectItem value="avec">Avec abonnement (&gt; 0 €)</SelectItem>
                  <SelectItem value="sans">Sans abonnement (0 €)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Row 2: Vendeur + Revendeur + Leaser + Année */}
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
              <MultiSelectFilter
                values={vendeurFilter}
                onValuesChange={setVendeurFilter}
                options={filters?.vendeurs || []}
                allLabel="Tous les vendeurs"
                placeholder="Vendeurs"
                data-testid="vendeur-filter"
              />
              
              <MultiSelectFilter
                values={revendeurFilter}
                onValuesChange={setRevendeurFilter}
                options={filters?.revendeurs || []}
                allLabel="Tous les revendeurs"
                placeholder="Revendeurs"
                data-testid="revendeur-filter"
              />
              
              <MultiSelectFilter
                values={leaserFilter}
                onValuesChange={setLeaserFilter}
                options={filters?.leasers || []}
                allLabel="Tous les leasers"
                placeholder="Leasers"
                data-testid="leaser-filter"
              />
              
              <Select value={yearFilter} onValueChange={setYearFilter}>
                <SelectTrigger data-testid="year-filter">
                  <SelectValue placeholder="Années" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les années</SelectItem>
                  {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Row 3: Actions */}
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2">
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={resetFilters} className="flex-1 sm:flex-none">
                  <Filter className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Réinitialiser</span>
                  <span className="sm:hidden">Reset</span>
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={fetchContracts} className="flex-1 sm:flex-none">
                  <RefreshCw className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Actualiser</span>
                  <span className="sm:hidden">Refresh</span>
                </Button>
              </div>
              <Button type="submit" className="btn-primary bg-primary text-primary-foreground">
                <Search className="w-4 h-4 mr-2" />
                Rechercher
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white border">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 sm:p-6 space-y-3">
              {[...Array(10)].map((_, i) => <div key={i} className="h-12 skeleton rounded"></div>)}
            </div>
          ) : contracts.length === 0 ? (
            <div className="p-8 sm:p-12 text-center">
              <FileText className="w-10 sm:w-12 h-10 sm:h-12 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground">Aucun contrat trouvé</p>
            </div>
          ) : (
            <>
              {/* Sort indicator for mobile */}
              <div className="block lg:hidden p-3 border-b bg-gray-50">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-center"
                  onClick={() => {
                    if (sortOrder === null) setSortOrder('asc');
                    else if (sortOrder === 'asc') setSortOrder('desc');
                    else setSortOrder(null);
                  }}
                >
                  {sortOrder === 'asc' && <><ArrowUpAZ className="w-4 h-4 mr-2 text-blue-600" /> Trié A → Z</>}
                  {sortOrder === 'desc' && <><ArrowDownAZ className="w-4 h-4 mr-2 text-blue-600" /> Trié Z → A</>}
                  {sortOrder === null && <><ArrowUpDown className="w-4 h-4 mr-2" /> Trier par nom</>}
                </Button>
              </div>
            
              {/* Mobile Cards View */}
              <div className="block lg:hidden divide-y">
                {sortedContracts.map((contract, i) => (
                  <div 
                    key={contract.id || i}
                    className={`p-3 hover:bg-gray-50 cursor-pointer ${selectedContracts.has(contract.id) ? 'bg-blue-50' : ''}`}
                    onClick={() => navigate(`/contrats/${contract.id}`)}
                    data-testid={`contract-card-${i}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={selectedContracts.has(contract.id)}
                          onCheckedChange={() => toggleSelectContract(contract.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground text-sm truncate">{contract.societe}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {getTypeBadge(contract.type)}
                            {getStatusBadge(contract.status, contract.statut_resiliation)}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-mono font-bold text-sm">{formatCurrency(contract.montant_abonnement)}</p>
                        <p className="text-xs text-muted-foreground">{formatDateFR(contract.date_fin)}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-dashed ml-8">
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        {contract.produit && <span>{contract.produit}</span>}
                        {contract.vendeur && <span>• {contract.vendeur}</span>}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={(e) => { e.stopPropagation(); handleEditContract(contract); }}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-500"
                          onClick={(e) => { e.stopPropagation(); handleDeleteContract(contract); }}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
              <Table className="min-w-[900px]">
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="w-12">
                      <Checkbox
                        checked={contracts.length > 0 && selectedContracts.size === contracts.length}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Sélectionner tout"
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    {visibleColumns.map((col) => (
                      <TableHead 
                        key={col.id}
                        className={`text-xs uppercase text-muted-foreground font-medium whitespace-nowrap select-none p-0 group transition-all
                          ${dragOverColId === col.id ? 'bg-blue-100' : ''}
                        `}
                        data-testid={`th-${col.id}`}
                      >
                        <div
                          draggable
                          onDragStart={(e) => handleHeaderDragStart(e, col.id)}
                          onDragOver={(e) => handleHeaderDragOver(e, col.id)}
                          onDragLeave={handleHeaderDragLeave}
                          onDrop={(e) => handleHeaderDrop(e, col.id)}
                          onDragEnd={handleHeaderDragEnd}
                          title="Glisser pour réorganiser les colonnes"
                          className={`h-10 px-2 flex items-center gap-1 cursor-move
                            ${col.align === 'right' ? 'justify-end' : ''}
                            ${draggedColId === col.id ? 'opacity-40' : ''}
                            ${dragOverColId === col.id ? 'border-l-2 border-blue-500' : ''}
                          `}
                          data-testid={`drag-handle-${col.id}`}
                        >
                          <GripVertical className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-50 transition-opacity flex-shrink-0" />
                          {col.id === 'societe' ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-auto p-0 hover:bg-transparent font-medium text-xs uppercase text-muted-foreground"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (sortOrder === null) setSortOrder('asc');
                                else if (sortOrder === 'asc') setSortOrder('desc');
                                else setSortOrder(null);
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                              draggable={false}
                              data-testid="sort-by-name-btn"
                            >
                              {col.label}
                              {sortOrder === 'asc' && <ArrowUpAZ className="w-4 h-4 ml-1 text-blue-600" />}
                              {sortOrder === 'desc' && <ArrowDownAZ className="w-4 h-4 ml-1 text-blue-600" />}
                              {sortOrder === null && <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />}
                            </Button>
                          ) : (
                            col.label
                          )}
                          {/* Bouton X pour masquer la colonne (visible au hover) */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setColumnVisible(col.id, false);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            draggable={false}
                            className="ml-1 opacity-0 group-hover:opacity-70 hover:!opacity-100 hover:text-red-600 transition-opacity flex-shrink-0"
                            title="Masquer cette colonne"
                            data-testid={`hide-column-${col.id}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      </TableHead>
                    ))}
                    {/* Bouton "+" pour ajouter une colonne masquée */}
                    {hiddenColumns.length > 0 && (
                      <TableHead className="w-8 p-0">
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="h-10 w-8 flex items-center justify-center text-muted-foreground hover:text-blue-600 hover:bg-blue-50 transition"
                              title="Ajouter une colonne"
                              data-testid="add-column-btn"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-1" align="end">
                            <p className="text-xs font-medium text-muted-foreground px-2 py-1.5 uppercase tracking-wider">
                              Colonnes masquées
                            </p>
                            <div className="max-h-72 overflow-y-auto">
                              {hiddenColumns.map((col) => (
                                <button
                                  key={col.id}
                                  type="button"
                                  onClick={() => setColumnVisible(col.id, true)}
                                  className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-blue-50 rounded transition text-left"
                                  data-testid={`show-column-${col.id}`}
                                >
                                  <Plus className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                                  <span className="flex-1">{col.label}</span>
                                </button>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </TableHead>
                    )}
                    <TableHead className="text-xs uppercase text-muted-foreground font-medium text-right whitespace-nowrap">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedContracts.map((contract, i) => (
                    <TableRow 
                      key={contract.id || i} 
                      className={`hover:bg-gray-50 ${selectedContracts.has(contract.id) ? 'bg-blue-50' : ''}`}
                      data-testid={`contract-row-${i}`}
                    >
                      <TableCell className="w-12">
                        <Checkbox
                          checked={selectedContracts.has(contract.id)}
                          onCheckedChange={() => toggleSelectContract(contract.id)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Sélectionner ${contract.societe}`}
                          data-testid={`select-contract-${i}`}
                        />
                      </TableCell>
                      {visibleColumns.map((col) => (
                        <TableCell 
                          key={col.id}
                          className={col.align === 'right' ? 'text-right' : ''}
                        >
                          {renderCell(contract, col.id)}
                        </TableCell>
                      ))}
                      {hiddenColumns.length > 0 && <TableCell className="w-8" />}
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/contrats/${contract.id}`)}
                            title="Voir les détails et documents"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEditContract(contract)}>
                                <Pencil className="w-4 h-4 mr-2" /> Modifier
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleViewLink(contract.societe)}>
                                <Link2 className="w-4 h-4 mr-2" /> Voir contrats & factures
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDeleteContract(contract)}
                                className="text-red-600"
                                disabled={deleting === contract.id}
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>

              {/* Pagination */}
              <div className="flex flex-col sm:flex-row items-center justify-between p-3 sm:p-4 border-t gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground hidden sm:inline">Afficher</span>
                  <Select value={perPage.toString()} onValueChange={(v) => { setPerPage(parseInt(v)); setPage(0); }}>
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground hidden sm:inline">par page</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    <span className="hidden sm:inline">Page </span>{page + 1}/{totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Contract Detail Modal */}
      <Dialog open={!!selectedContract} onOpenChange={() => setSelectedContract(null)}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Détail du contrat
            </DialogTitle>
          </DialogHeader>
          {selectedContract && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Société</p>
                  <p className="font-medium">{selectedContract.societe}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Statut</p>
                  {getStatusBadge(selectedContract.status)}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Type</p>
                  <p className="font-medium">{selectedContract.type}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Produit</p>
                  <p className="font-medium">{selectedContract.produit}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Date signature</p>
                  <p className="font-medium">{formatDateFR(selectedContract.date_signature)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Date fin</p>
                  <p className="font-medium">{formatDateFR(selectedContract.date_fin)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Durée</p>
                  <p className="font-medium">{selectedContract.duree_mois} mois</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">MRR</p>
                  <p className="font-mono font-bold text-lg">{formatCurrency(selectedContract.montant_abonnement)}/mois</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-2">Détails financiers</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Gestion Interne</p>
                    <p className="font-mono">{formatCurrency(selectedContract.abonnement_gestion_interne)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Abonnement Financé</p>
                    <p className="font-mono">{formatCurrency(selectedContract.abonnement_finance)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Leaser</p>
                    <p className="font-medium">{selectedContract.leaser || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">CA Leaser</p>
                    <p className="font-mono">{formatCurrency(selectedContract.ca_leaser)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Vendeur</p>
                    <p className="font-medium">{selectedContract.vendeur || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Revendeur</p>
                    <p className="font-medium">{selectedContract.revendeur || '-'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Client Link Modal */}
      <Dialog open={!!clientLink} onOpenChange={() => setClientLink(null)}>
        <DialogContent className="bg-white max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Link2 className="w-5 h-5 text-primary" />
              Contrats & Factures - {clientLink?.client}
            </DialogTitle>
          </DialogHeader>
          {loadingLink ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : clientLink && (
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-6">
                {/* Contracts */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">Contrats ({clientLink.contracts.count})</h3>
                    <span className="text-sm text-muted-foreground">
                      MRR: {formatCurrency(clientLink.contracts.total_mrr)}
                    </span>
                  </div>
                  {clientLink.contracts.items.length > 0 ? (
                    <div className="space-y-2">
                      {clientLink.contracts.items.map((c) => (
                        <div key={c.id || c.produit + c.date_fin} className="p-3 bg-gray-50 rounded-lg flex justify-between items-center">
                          <div>
                            <p className="font-medium">{c.produit}</p>
                            <p className="text-xs text-muted-foreground">{c.type} • {formatDateFR(c.date_fin)}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono font-bold">{formatCurrency(c.montant_abonnement)}/mois</p>
                            {getStatusBadge(c.status)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">Aucun contrat</p>
                  )}
                </div>

                {/* Invoices */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">Dernières factures ({clientLink.invoices.count})</h3>
                    <span className="text-sm text-muted-foreground">
                      Total: {formatCurrency(clientLink.invoices.total_ht)}
                    </span>
                  </div>
                  {clientLink.invoices.items.length > 0 ? (
                    <div className="space-y-2">
                      {clientLink.invoices.items.slice(0, 10).map((inv) => (
                        <div 
                          key={inv.id || inv.numero} 
                          className="p-3 bg-blue-50 rounded-lg flex justify-between items-center cursor-pointer hover:bg-blue-100 transition-colors"
                          onClick={() => {
                            setSelectedInvoice(inv);
                            if (inv.has_pdf) {
                              setPdfModalOpen(true);
                            }
                          }}
                        >
                          <div className="flex items-center gap-3">
                            {inv.has_pdf && <Paperclip className="w-4 h-4 text-blue-500" />}
                            <div>
                              <p className="font-medium font-mono text-blue-700 hover:underline">{inv.numero}</p>
                              <p className="text-xs text-muted-foreground">{inv.type} • {formatDateFR(inv.date)}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-mono font-bold">{formatCurrency(inv.total_ht)}</p>
                            <p className="text-xs text-muted-foreground">TTC: {formatCurrency(inv.total_ttc)}</p>
                          </div>
                        </div>
                      ))}
                      {clientLink.invoices.count > 10 && (
                        <p className="text-center text-sm text-muted-foreground py-2">
                          ... et {clientLink.invoices.count - 10} autres factures
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">Aucune facture</p>
                  )}
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        open={pdfModalOpen}
        onOpenChange={setPdfModalOpen}
        invoiceNumero={selectedInvoice?.numero}
        invoiceClient={selectedInvoice?.client}
      />

      {/* Import Contracts Modal */}
      <ImportContractsModal 
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={handleImportSuccess}
      />

      {/* Create/Edit Contract Modal */}
      <ContractFormModal
        open={formModalOpen}
        onOpenChange={setFormModalOpen}
        contract={editingContract}
        onSuccess={handleFormSuccess}
        filters={filters}
      />
    </div>
  );
}
